import React from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Avatar, Box, Button, CircularProgress, IconButton, LinearProgress, Stack, Typography } from '@mui/material';
import {
  Close as CloseIcon, Edit as EditIcon, DeleteOutline as DeleteIcon, FitnessCenter as ExerciseIcon,
  CalendarMonth as CalendarIcon, Timer as TimeIcon, Repeat as RepsIcon, Scale as WeightIcon,
  DirectionsRun as DistanceIcon, InfoOutlined as InfoIcon
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { es } from 'date-fns/locale';
import { premiumButtonSx } from 'common/theme/button/premiumButtonSx';

const PARAM = {
  TIME: { icon: <TimeIcon sx={{ fontSize: 15 }} />, label: 'Time' },
  REPS: { icon: <RepsIcon sx={{ fontSize: 15 }} />, label: 'Reps' },
  WEIGHT: { icon: <WeightIcon sx={{ fontSize: 15 }} />, label: 'Peso' },
  DISTANCE: { icon: <DistanceIcon sx={{ fontSize: 15 }} />, label: 'Dist.' }
};

const fmtDate = (d) => {
  try { return format(parseISO(String(d).slice(0, 10)), 'dd/MM/yyyy', { locale: es }); } catch { return d; }
};

const rawVal = (set, type) => {
  switch (type) {
    case 'TIME': return set.time_seconds;
    case 'REPS': return set.repetitions;
    case 'WEIGHT': return set.weight_kg;
    case 'DISTANCE': return set.distance_km;
    default: return null;
  }
};

const fmtVal = (v, type) => {
  if (v == null) return '—';
  switch (type) {
    case 'TIME': return `${Math.floor(v / 60)}m ${String(v % 60).padStart(2, '0')}s`;
    case 'REPS': return `${v} reps`;
    case 'WEIGHT': return `${v} kg`;
    case 'DISTANCE': return `${v} km`;
    default: return v;
  }
};

/** Gauge circular por parámetro: relleno = promedio/mejor (mejor serie del período = 100%). */
function ParamGauge({ type, pct, best = null }) {
  return (
    <Stack alignItems="center" spacing={0.75} sx={{ minWidth: 80 }}>
      <Box sx={{ position: 'relative', display: 'inline-flex' }}>
        <CircularProgress variant="determinate" value={100} size={64} thickness={4} sx={{ color: 'hairline.main' }} />
        <CircularProgress variant="determinate" value={pct} size={64} thickness={4} sx={{ color: 'gold.main', position: 'absolute', left: 0, '& .MuiCircularProgress-circle': { strokeLinecap: 'round' } }} />
        <Box sx={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Typography variant="caption" sx={{ fontWeight: 800, color: 'text.primary' }}>{pct}%</Typography>
        </Box>
      </Box>
      <Stack direction="row" alignItems="center" spacing={0.4} sx={{ color: 'gold.main' }}>
        {PARAM[type]?.icon}
        <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary' }}>{PARAM[type]?.label}</Typography>
      </Stack>
      <Typography variant="caption" sx={{ color: 'text.secondary', fontWeight: 600 }}>{fmtVal(best, type)}</Typography>
    </Stack>
  );
}
ParamGauge.propTypes = { type: PropTypes.string.isRequired, pct: PropTypes.number.isRequired, best: PropTypes.number };
function PerformanceDetailPanel({ row = null, onEdit, onDelete, onClose }) {
  const { t } = useTranslation(['performances', 'buttons']);
  if (!row) return <Box />;

  const paramKeys = (row.exercise?.parameters || []).map((p) => p.parameter_type);
  const sets = row.sets || [];

  // Por parámetro: relleno = promedio/mejor (mejor serie = 100%), label = mejor valor.
  const gauges = paramKeys.map((type) => {
    const nums = sets.map((s) => rawVal(s, type)).filter((v) => v != null && v !== '').map(Number).filter((n) => !Number.isNaN(n));
    const best = nums.length ? Math.max(...nums) : 0;
    const avg = nums.length ? nums.reduce((a, b) => a + b, 0) / nums.length : 0;
    const pct = best > 0 ? Math.round((avg / best) * 100) : 0;
    return { type, pct, best };
  });

  // Barra de cada serie: % de valores logrados respecto al MEJOR de cada parámetro (promedio).
  const setPct = (set) => {
    if (paramKeys.length === 0) return 0;
    const ratios = gauges.map(({ type, best }) => {
      const v = Number(rawVal(set, type));
      if (best > 0 && !Number.isNaN(v)) return Math.min(v / best, 1);
      return 0;
    });
    return Math.round((ratios.reduce((a, b) => a + b, 0) / ratios.length) * 100);
  };

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <Typography variant="h6" sx={{ fontWeight: 800 }}>{t('dynamicPerformances.detailTitle', { ns: 'performances', defaultValue: 'Detalle del rendimiento' })}</Typography>
        <IconButton size="small" onClick={onClose} sx={{ color: 'text.secondary' }}><CloseIcon fontSize="small" /></IconButton>
      </Stack>

      {/* Cliente + ejercicio + período (compacto) */}
      <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 1 }}>
        <Avatar src={row.client?.photo} sx={{ width: 44, height: 44, bgcolor: 'gold.soft', color: 'gold.main' }}>{row.client?.first_name?.[0]}</Avatar>
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 800, lineHeight: 1.2 }}>{row.client?.first_name} {row.client?.last_name}</Typography>
          <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: 'gold.main' }}>
            <ExerciseIcon sx={{ fontSize: 15 }} />
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.exercise?.name}</Typography>
          </Stack>
        </Box>
      </Stack>
      <Stack direction="row" spacing={0.6} alignItems="center" sx={{ color: 'text.secondary', mb: 2 }}>
        <CalendarIcon sx={{ fontSize: 15 }} />
        <Typography variant="caption" sx={{ fontWeight: 600 }}>{fmtDate(row.period_start_date)} – {fmtDate(row.period_end_date)}</Typography>
      </Stack>

      {/* Gauges por parámetro */}
      {gauges.length > 0 && (
        <Stack direction="row" spacing={2} justifyContent="center" flexWrap="wrap" useFlexGap sx={{ p: 2, mb: 2, borderRadius: 2, bgcolor: 'surface.app', border: '1px solid', borderColor: 'hairline.main' }}>
          {gauges.map((g) => <ParamGauge key={g.type} type={g.type} pct={g.pct} best={g.best} />)}
        </Stack>
      )}

      {/* Series */}
      <Typography variant="subtitle2" sx={{ fontWeight: 800, color: 'gold.main', mb: 1 }}>{t('dynamicPerformances.sets', { ns: 'performances', defaultValue: 'Series' })}</Typography>
      <Stack spacing={1}>
        {sets.length === 0 ? (
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>{t('dynamicPerformances.modal.noSetsMessage', { ns: 'performances', defaultValue: 'Sin series registradas.' })}</Typography>
        ) : sets.map((set, i) => {
          const p = setPct(set);
          return (
            <Box key={set.id || `set-${i}`} sx={{ p: 1.25, borderRadius: 2, bgcolor: 'surface.app', border: '1px solid', borderColor: 'hairline.main' }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography variant="caption" sx={{ fontWeight: 800, color: 'gold.main' }}>{t('dynamicPerformances.modal.setNumber', { ns: 'performances', number: set.set_number ?? i + 1, defaultValue: `Serie ${set.set_number ?? i + 1}` })}</Typography>
                <Stack direction="row" spacing={0.75}>
                  {paramKeys.map((type) => {
                    const v = rawVal(set, type);
                    if (v == null) return null;
                    return (
                      <Stack key={type} direction="row" spacing={0.3} alignItems="center" sx={{ color: 'text.secondary' }}>
                        {PARAM[type]?.icon}
                        <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.primary' }}>{fmtVal(v, type)}</Typography>
                      </Stack>
                    );
                  })}
                </Stack>
              </Stack>
              <LinearProgress variant="determinate" value={p} sx={{ height: 5, borderRadius: 3, bgcolor: 'hairline.main', '& .MuiLinearProgress-bar': { bgcolor: 'gold.main', borderRadius: 3 } }} />
            </Box>
          );
        })}
      </Stack>

      {row.observations && (
        <Stack direction="row" spacing={1.25} alignItems="flex-start" sx={{ mt: 2 }}>
          <Box sx={{ color: 'gold.main', mt: 0.25, display: 'flex' }}><InfoIcon fontSize="small" /></Box>
          <Box>
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 700 }}>{t('dynamicPerformances.modal.observations', { ns: 'performances', defaultValue: 'Observaciones' })}</Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.25 }}>{row.observations}</Typography>
          </Box>
        </Stack>
      )}

      {row.trainer && (
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mt: 2 }}>
          <Avatar src={row.trainer?.photo} sx={{ width: 30, height: 30 }}>{row.trainer?.first_name?.[0]}</Avatar>
          <Box>
            <Typography variant="body2" sx={{ fontWeight: 700 }}>{row.trainer?.first_name} {row.trainer?.last_name}</Typography>
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>{t('dynamicPerformances.table.trainer', { ns: 'performances', defaultValue: 'Entrenador' })}</Typography>
          </Box>
        </Stack>
      )}

      <Stack direction="row" spacing={1.5} sx={{ mt: 2.5 }}>
        <Button fullWidth startIcon={<EditIcon />} onClick={() => onEdit(row)} sx={{ ...premiumButtonSx, py: 1, fontSize: '0.9rem' }}>{t('edit', { ns: 'buttons', defaultValue: 'Editar' })}</Button>
        <Button variant="outlined" color="inherit" startIcon={<DeleteIcon />} onClick={() => onDelete(row)} sx={{ py: 1, borderRadius: 2, color: (th) => th.palette.sessionStatus.absent.fg, borderColor: 'hairline.strong', fontWeight: 700, textTransform: 'none' }}>{t('delete', { ns: 'buttons', defaultValue: 'Eliminar' })}</Button>
      </Stack>
    </Box>
  );
}

PerformanceDetailPanel.propTypes = {
  row: PropTypes.object,
  onEdit: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired
};

export default PerformanceDetailPanel;
